import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useEmployees } from "../../hooks/useEmployees";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import DateCalendar from "../DateCalendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";

export interface PatchTestData {
  id?: number;
  title: string;
  tested: string;
  testedBy: string;
  status: "pending" | "passed" | "failed";
  description: string;
  expiresOn?: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PatchTestData) => void;
  onDelete?: (id: number) => void;
  editData?: PatchTestData | null;
  loading?: boolean;
  deleting?: boolean;
}

const StatusIcon = ({ status }: { status: string }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "#f97316";
      case "passed":
        return "#22c55e";
      case "failed":
        return "#ec4899";
      default:
        return "#9ca3af";
    }
  };

  const color = getStatusColor(status);

  return (
    <div className="flex items-center justify-center w-12 h-12 rounded-full" style={{ backgroundColor: `${color}20` }}>
      <svg width="24" height="24" viewBox="0 0 24 24">
        {[0, 1, 2].map((row) =>
          [0, 1, 2].map((col) => {
            const x = 8 + col * 4;
            const y = 8 + row * 4;
            return (
              <circle
                key={`${row}-${col}`}
                cx={x}
                cy={y}
                r="1.5"
                fill={color}
              />
            );
          })
        )}
      </svg>
    </div>
  );
};

export default function PatchTestModal({
  isOpen,
  onClose,
  onSave,
  onDelete,
  editData,
  loading = false,
  deleting = false,
}: Props) {
  const { user } = useAuth();
  const { staffList } = useEmployees();
  const [formData, setFormData] = useState<PatchTestData>({
    title: "",
    tested: "",
    testedBy: "",
    status: "pending",
    description: "",
  });

  useEffect(() => {
    if (editData) {
      // If testedBy is a name, try to find its ID in staffList
      let testedByValue = editData.testedBy;
      if (testedByValue && isNaN(Number(testedByValue))) {
        const staff = staffList.find(s => s.name === testedByValue);
        if (staff) testedByValue = staff.id?.toString() || testedByValue;
      }
      setFormData({ ...editData, testedBy: testedByValue || "" });
    }
    else {
      // Set default date to today
      const today = new Date();
      // Use YYYY-MM-DD format
      const formattedDate = today.toISOString().split('T')[0];

      // Calculate initial expiry (6 months)
      const expiryDate = new Date(today);
      expiryDate.setMonth(expiryDate.getMonth() + 6);
      const formattedExpiry = expiryDate.toISOString().split('T')[0];

      setFormData({
        title: "",
        tested: formattedDate,
        testedBy: user?.id?.toString() || user?.user_id?.toString() || "",
        status: "pending",
        description: "",
        expiresOn: formattedExpiry
      });
    }
  }, [editData, isOpen, user?.id, user?.user_id, staffList]);

  useEffect(() => {
    if (formData.tested) {
      // Calculate expiration date (6 months from tested date)
      try {
        const testedDate = new Date(formData.tested);
        if (!isNaN(testedDate.getTime())) {
          const expiryDate = new Date(testedDate);
          expiryDate.setMonth(expiryDate.getMonth() + 6);
          const formatted = expiryDate.toISOString().split('T')[0];
          setFormData(prev => ({ ...prev, expiresOn: formatted }));
        }
      } catch (e) {
        console.error("Error calculating expiry date:", e);
      }
    }
  }, [formData.tested]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleDelete = () => {
    if (editData?.id && onDelete) {
      onDelete(editData.id);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">
            {editData ? "Edit patch / skin test" : "Patch / skin test"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-900">
                Title
              </label>
              <span className="text-sm text-gray-500">
                {formData.title.length}/100
              </span>
            </div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              placeholder="e.g. Tint patch test"
              maxLength={100}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
              required
            />
          </div>

          {/* Tested & Tested By */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Tested
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${!formData.tested && "text-gray-500"
                      }`}
                  >
                    <span className="block truncate">
                      {formData.tested ? new Date(formData.tested).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "dd/mm/yyyy"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-gray-400" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <DateCalendar
                    selectedDate={formData.tested ? new Date(formData.tested) : new Date()}
                    onSelectDate={(date) => {
                      // Adjust for timezone offset to prevent day shifting
                      const offset = date.getTimezoneOffset();
                      const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
                      setFormData({ ...formData, tested: adjustedDate.toISOString().split('T')[0] });
                    }}
                  />
                </PopoverContent>
              </Popover>
              {formData.expiresOn && formData.expiresOn !== "N/A" && (
                <p className="text-sm text-gray-500 mt-2">
                  Expires on {new Date(formData.expiresOn).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Tested by
              </label>
              <Select
                value={formData.testedBy}
                onValueChange={(value) =>
                  setFormData({ ...formData, testedBy: value })
                }
              >
                <SelectTrigger className="w-full h-[42px] border-gray-300 bg-white rounded-lg focus:ring-purple-500 focus:border-purple-500">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 z-[1001]">
                  {/* Fallback for when the value doesn't match any staff member in the list */}
                  {formData.testedBy && !staffList.find(s => s.id?.toString() === formData.testedBy || s.name === formData.testedBy) && (
                    <SelectItem value={formData.testedBy}>
                      {isNaN(Number(formData.testedBy)) ? formData.testedBy : `Staff Member (${formData.testedBy})`}
                    </SelectItem>
                  )}
                  {staffList.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id?.toString() || staff.name}>
                      {staff.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">
              Status
            </label>
            <div className="grid grid-cols-3 gap-4">
              {(["pending", "passed", "failed"] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setFormData({ ...formData, status })}
                  className={`p-4 border-2 rounded-xl flex flex-col items-center gap-2 transition ${formData.status === status
                    ? "border-purple-500 bg-purple-50"
                    : "border-gray-200 hover:border-gray-300"
                    }`}
                >
                  <StatusIcon status={status} />
                  <span className="text-sm font-medium capitalize">
                    {status}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-900">
                Description
              </label>
              <span className="text-sm text-gray-500">
                {formData.description.length}/200
              </span>
            </div>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Enter the patch test details, e.g. product name or the updates history"
              maxLength={200}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 gap-3">
            {editData?.id && onDelete ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting || loading}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition ${
                  deleting
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                }`}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || deleting}
                className={`px-6 py-2 rounded-lg text-sm font-medium transition ${loading
                  ? "bg-gray-400 text-white cursor-not-allowed"
                  : "bg-[#885ABB] text-white hover:bg-[#6f47a2]"
                  }`}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
